import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import DistributorOrderForm from './DistributorOrderForm';

export default function DistributorHeldOrder({ order, admin, onChanged, onBusy }) {
  const t = useT();
  const session = useRef(captureSession()).current;
  const mounted = useRef(true);
  const flight = useRef(false);
  const sequence = useRef(0);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [formState, setFormState] = useState({ busy: false, uncertain: false });
  const current = () => mounted.current && isSessionCurrent(session);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, []);
  useEffect(() => {
    onBusy?.(loading || formState.busy);
  }, [loading, formState.busy, onBusy]);
  const load = async () => {
    if (flight.current || !current()) return;
    flight.current = true;
    const generation = ++sequence.current;
    setLoading(true);
    setError('');
    setOptions(null);
    setEditing(false);
    setConfirmCancel(false);
    setFormState({ busy: false, uncertain: false });
    try {
      const row = (await distributorService.heldOrderOptions(order._id, admin))?.data?.data;
      if (!current() || generation !== sequence.current) return;
      if (
        row?._id !== order._id ||
        !Number.isSafeInteger(row.revision) ||
        row.revision < 0 ||
        row.status !== 'submitted'
      )
        throw new Error('Unconfirmed order');
      setOptions(row);
    } catch {
      if (current())
        setError('Could not load the current order. Refresh your orders before trying again.');
    } finally {
      flight.current = false;
      if (current()) setLoading(false);
    }
  };
  const cancel = async () => {
    if (flight.current || !current() || !options?.cancellable || formState.uncertain) return;
    flight.current = true;
    setLoading(true);
    setError('');
    try {
      const row = (
        await distributorService.cancelHeldOrder(
          order._id,
          { expectedRevision: options.revision },
          admin
        )
      )?.data?.data;
      if (!current()) return;
      if (row?._id !== order._id || row.status !== 'cancelled')
        throw new Error('Unconfirmed cancellation');
      onChanged();
    } catch {
      if (current()) {
        setOptions(null);
        setConfirmCancel(false);
        setError('Cancellation could not be confirmed. Refresh your orders before trying again.');
      }
    } finally {
      flight.current = false;
      if (current()) setLoading(false);
    }
  };
  return (
    <section
      className="mb-5 rounded-lg border border-slate-200 p-3 space-y-3"
      aria-label={t('Processing order actions')}
    >
      <p className="text-sm">
        {t(
          'This order has been received and is being processed. It has not been approved or sent.'
        )}
      </p>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {t(error)}
        </p>
      )}
      <button
        type="button"
        className="me-dw-link"
        disabled={loading || formState.busy}
        onClick={load}
      >
        {t(loading ? 'Loading...' : options ? 'Reload saved order' : 'Review processing order')}
      </button>
      {options && (
        <>
          {options.editable && !editing && (
            <button type="button" className="me-dw-primary ml-3" onClick={() => setEditing(true)}>
              {t('Review order changes')}
            </button>
          )}
          {editing && options.editable && (
            <DistributorOrderForm
              key={`${options._id}:${options.revision}`}
              clinicMode
              heldOrder={options}
              clinicId={options.clinicId}
              t={t}
              onSubmitted={onChanged}
              onStateChange={setFormState}
            />
          )}
          {options.cancellable && !confirmCancel && (
            <button
              type="button"
              className="me-dw-link ml-3"
              disabled={loading || formState.busy || formState.uncertain}
              onClick={() => setConfirmCancel(true)}
            >
              {t('Cancel processing order')}
            </button>
          )}
          {confirmCancel && (
            <div className="space-y-3">
              <p>{t('Cancel this order? This cannot be undone.')}</p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  className="me-dw-primary"
                  disabled={loading || formState.busy || formState.uncertain}
                  onClick={cancel}
                >
                  {t('Confirm cancellation')}
                </button>
                <button
                  type="button"
                  className="me-dw-link"
                  disabled={loading}
                  onClick={() => setConfirmCancel(false)}
                >
                  {t('Keep order')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
